import { useState, type ReactNode } from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';

/** Gemeinsames Gerüst der sortierbaren Tabellen (Kunden- und Schlüssel-Sicht):
 *  gleiche Optik wie die Tabellen im Nutzung-Tab, Sortierung klientseitig. */

export interface Spalte<T> {
  key: string;
  titel: string;
  rechts?: boolean;
  wert: (zeile: T) => string | number | null;
  anzeige?: (zeile: T) => ReactNode;
}

export function nutzeSortierung<T>(spalten: Spalte<T>[], startKey: string) {
  const [sortierung, setSortierung] = useState<{ key: string; absteigend: boolean }>({ key: startKey, absteigend: true });

  const sortiere = (zeilen: T[]): T[] => {
    const spalte = spalten.find(s => s.key === sortierung.key);
    if (!spalte) return zeilen;
    return [...zeilen].sort((a, b) => {
      const wa = spalte.wert(a);
      const wb = spalte.wert(b);
      if (wa === null && wb === null) return 0;
      if (wa === null) return 1; // Leeres immer ans Ende, egal welche Richtung
      if (wb === null) return -1;
      const vergleich = typeof wa === 'number' && typeof wb === 'number'
        ? wa - wb
        : String(wa).localeCompare(String(wb), 'de');
      return sortierung.absteigend ? -vergleich : vergleich;
    });
  };

  const umschalten = (key: string) => {
    setSortierung(s => (s.key === key ? { key, absteigend: !s.absteigend } : { key, absteigend: true }));
  };

  return { sortierung, sortiere, umschalten };
}

export function SortierKopf<T>({ spalten, sortierung, umschalten, darkMode }: {
  spalten: Spalte<T>[];
  sortierung: { key: string; absteigend: boolean };
  umschalten: (key: string) => void;
  darkMode: boolean;
}) {
  return (
    <thead>
      <tr className={`border-y ${darkMode ? 'border-slate-700 bg-slate-700/30' : 'border-slate-200 bg-slate-50'}`}>
        {spalten.map(s => (
          <th key={s.key} className={`px-4 py-2 ${s.rechts ? 'text-right' : 'text-left'}`}>
            <button
              type="button"
              onClick={() => umschalten(s.key)}
              className={`inline-flex items-center gap-1 font-semibold ${s.rechts ? 'flex-row-reverse' : ''} cursor-pointer`}
            >
              {s.titel}
              {sortierung.key === s.key && (
                sortierung.absteigend ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />
              )}
            </button>
          </th>
        ))}
      </tr>
    </thead>
  );
}
